import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Search, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

interface FloristProfile {
  id: string;
  store_name: string;
  storefront_image_url?: string;
  cover_image_url?: string;
  suburb?: string;
  state?: string;
  featured: boolean;
}

interface Product {
  id: string;
  title: string;
  price: number;
  main_image_url?: string;
  store_id: string;
  florist_name?: string;
  featured: boolean;
}

export default function FeaturedContentManagement() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [florists, setFlorists] = useState<FloristProfile[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [featuredFlorists, setFeaturedFlorists] = useState<FloristProfile[]>([]);
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const [savingFlorists, setSavingFlorists] = useState(false);
  const [savingProducts, setSavingProducts] = useState(false);

  // Fetch data when component mounts
  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!user || !isAdmin) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [user, isAdmin, authLoading]);

  // Filter florists based on search query
  const filteredFlorists = florists.filter(florist => 
    florist.store_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    florist.suburb?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    florist.state?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter products based on search query
  const filteredProducts = products.filter(product => 
    product.title.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
    product.florist_name?.toLowerCase().includes(productSearchQuery.toLowerCase())
  );

  // Fetch both florists and products
  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchFlorists(), fetchProducts()]);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast({
        title: "Error",
        description: "Failed to load content",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch florists from Supabase
  const fetchFlorists = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('store_profiles')
        .select('id, store_name, featured, storefront_image_url, cover_image_url, suburb, state')

      if (error) throw error;

      const floristData = data || [];
      setFlorists(floristData);
      setFeaturedFlorists(floristData.filter(f => f.featured));
    } catch (error) {
      console.error("Error fetching florists:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Fetch products from Supabase
  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          id, 
          title, 
          price, 
          main_image_url,
          store_id, 
          featured,
          store_profiles(store_name)
        `)
        .eq('active', true)
        .order('title', { ascending: true });

      if (error) throw error;

      const productsData = (data || []).map(product => ({
        id: product.id,
        title: product.title,
        price: product.price,
        main_image_url: product.main_image_url,
        store_id: product.store_id,
        florist_name: product.store_profiles?.store_name || "Unknown Florist",
        featured: product.featured || false
      }));

      setProducts(productsData);
      setFeaturedProducts(productsData.filter(p => p.featured));
    } catch (error) {
      console.error("Error fetching products:", error);
      throw error;
    }
  };

  // Toggle featured status for a florist
  const toggleFloristFeatured = (id: string) => {
    setFlorists(prev => 
      prev.map(florist => 
        florist.id === id 
          ? { ...florist, featured: !florist.featured } 
          : florist
      )
    );
  };

  // Toggle featured status for a product
  const toggleProductFeatured = (id: string) => {
    setProducts(prev => 
      prev.map(product => 
        product.id === id 
          ? { ...product, featured: !product.featured } 
          : product
      )
    );
  };

  // Save featured florists to Supabase
  const saveFloristChanges = async () => {
    setSavingFlorists(true);
    try {
      // Get the IDs of florists that were toggled
      const updatedFlorists = florists.filter(florist => {
        const wasFeatured = featuredFlorists.some(f => f.id === florist.id);
        return wasFeatured !== florist.featured;
      });

      // Update each florist's featured status
      for (const florist of updatedFlorists) {
        const { error } = await supabase
          .from('store_profiles')
          .update({ featured: florist.featured })
          .eq('id', florist.id);

        if (error) throw error;
      }

      setFeaturedFlorists(florists.filter(f => f.featured));
      toast({
        title: "Success",
        description: "Featured florists updated successfully",
      });
    } catch (error) {
      console.error("Error saving florist changes:", error);
      toast({
        title: "Error",
        description: "Failed to update featured florists",
        variant: "destructive",
      });
    } finally {
      setSavingFlorists(false);
    }
  };

  // Save featured products to Supabase
  const saveProductChanges = async () => {
    setSavingProducts(true);
    try {
      // First, unfeatured ALL products
      const { error: unfeaturedError } = await supabase
        .from('products')
        .update({ featured: false })
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Update all products

      if (unfeaturedError) {
        console.error('Error unfeaturing all products:', unfeaturedError);
        throw unfeaturedError;
      }

      // Then, feature only the selected ones
      const productsToFeature = products.filter(p => p.featured);
      console.log('Products to feature:', productsToFeature.map(p => ({ id: p.id, title: p.title })));

      for (const product of productsToFeature) {
        console.log(`Featuring product ${product.title}`);
        const { error, data } = await supabase
          .from('products')
          .update({ featured: true })
          .eq('id', product.id)
          .select();

        if (error) {
          console.error(`Error featuring product ${product.id}:`, error);
          throw error;
        }
        console.log(`Successfully featured product:`, data);
      }

      setFeaturedProducts(productsToFeature);
      console.log(`Featured ${productsToFeature.length} products total`);
      toast({
        title: "Success",
        description: `Featured products updated successfully (${productsToFeature.length} products featured)`,
      });
    } catch (error) {
      console.error("Error saving product changes:", error);
      toast({
        title: "Error",
        description: "Failed to update featured products",
        variant: "destructive",
      });
    } finally {
      setSavingProducts(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout currentPage="Featured Content">
        <div className="flex justify-center items-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage="Featured Content">
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-[#4A4F41]">Featured Content</h1>
          <p className="text-muted-foreground mt-2">Manage featured florists and products on the homepage</p>
        </div>

        <Tabs defaultValue="florists" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="florists">Featured Florists</TabsTrigger>
            <TabsTrigger value="products">Featured Products</TabsTrigger>
          </TabsList>

          {/* Featured Florists Tab */}
          <TabsContent value="florists" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Florists</CardTitle>
                    <CardDescription>Select up to 6 florists to feature on the homepage</CardDescription>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchFlorists}
                      className="flex items-center"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Refresh
                    </Button>
                    <Button
                      onClick={saveFloristChanges}
                      disabled={savingFlorists}
                      className="bg-[#967F81] hover:bg-[#843329] text-white"
                    >
                      {savingFlorists ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save Changes"
                      )}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="relative w-full mb-6">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Search florists..."
                    className="pl-8 bg-white border-gray-200"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]">Featured</TableHead>
                        <TableHead>Store</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Preview</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredFlorists.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-muted-foreground">
                            No florists found
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredFlorists.map((florist) => (
                          <TableRow key={florist.id}>
                            <TableCell>
                              <Checkbox
                                checked={florist.featured}
                                onCheckedChange={() => toggleFloristFeatured(florist.id)}
                              />
                            </TableCell>
                            <TableCell className="font-medium">{florist.store_name}</TableCell>
                            <TableCell>
                              {florist.suburb}{florist.suburb && florist.state ? ", " : ""}{florist.state}
                            </TableCell>
                            <TableCell>
                              {florist.storefront_image_url || florist.cover_image_url ? (
                                <img 
                                  src={florist.storefront_image_url || florist.cover_image_url} 
                                  alt={florist.store_name} 
                                  className="h-12 w-16 object-cover rounded-md" 
                                />
                              ) : (
                                <div className="h-12 w-16 bg-gray-200 rounded-md flex items-center justify-center text-xs text-gray-500">No image</div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Featured Products Tab */}
          <TabsContent value="products" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Products</CardTitle>
                    <CardDescription>Select up to 8 products to feature on the homepage</CardDescription>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchProducts}
                      className="flex items-center"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Refresh
                    </Button>
                    <Button
                      onClick={saveProductChanges}
                      disabled={savingProducts}
                      className="bg-[#967F81] hover:bg-[#843329] text-white"
                    >
                      {savingProducts ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save Changes"
                      )}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="relative w-full mb-6">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Search products..."
                    className="pl-8 bg-white border-gray-200"
                    value={productSearchQuery}
                    onChange={(e) => setProductSearchQuery(e.target.value)}
                  />
                </div>

                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]">Featured</TableHead>
                        <TableHead>Product</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Florist</TableHead>
                        <TableHead>Preview</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredProducts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground">
                            No products found
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredProducts.map((product) => (
                          <TableRow key={product.id}>
                            <TableCell>
                              <Checkbox
                                checked={product.featured}
                                onCheckedChange={() => toggleProductFeatured(product.id)}
                              />
                            </TableCell>
                            <TableCell className="font-medium">{product.title}</TableCell>
                            <TableCell>${product.price.toFixed(2)}</TableCell>
                            <TableCell>{product.florist_name}</TableCell>
                            <TableCell>
                              {product.main_image_url ? (
                                <img
                                  src={product.main_image_url}
                                  alt={product.title}
                                  className="h-12 w-16 object-cover rounded-md"
                                />
                              ) : (
                                <div className="h-12 w-16 bg-gray-200 rounded-md flex items-center justify-center text-xs text-gray-500">
                                  No image
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
} 